import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { dataService } from '@/services/dataService';
import { Creator, Followup } from '@/types/entities';
import { useI18n } from '@/i18n';
import { useToast } from '@/components/ui/Toast';
import { ConfirmModal } from '@/components/ui/ConfirmModal';

const VIEWS = ['today', 'overdue', 'upcoming', 'completed', 'all'] as const;
type ViewKey = (typeof VIEWS)[number];
const TODAY = '2026-08-19'; // matches ENV's known current date context; real deployments would use new Date()

function matchesView(f: Followup, view: ViewKey): boolean {
  switch (view) {
    case 'today': return f.status === 'open' && f.dueDate === TODAY;
    case 'overdue': return f.status === 'open' && f.dueDate < TODAY;
    case 'upcoming': return f.status === 'open' && f.dueDate > TODAY;
    case 'completed': return f.status === 'done';
    default: return true;
  }
}

export function FollowUpsPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const push = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [followups, setFollowups] = useState<Followup[]>([]);
  const [view, setView] = useState<ViewKey>('today');
  const [deleteTarget, setDeleteTarget] = useState<Followup | null>(null);
  const [formOpen, setFormOpen] = useState(searchParams.get('new') === '1');
  const [creators, setCreators] = useState<Creator[]>([]);
  const [newCreatorId, setNewCreatorId] = useState('');
  const [newTitle, setNewTitle] = useState('');

  function reload() {
    dataService.listFollowups().then(setFollowups);
  }
  useEffect(reload, []);
  useEffect(() => {
    if (formOpen) dataService.listCreators().then(setCreators);
  }, [formOpen]);

  async function submitNew() {
    const creator = creators.find((c) => c.id === newCreatorId);
    if (!creator || !newTitle.trim()) return;
    const dueDate = new Date().toISOString().slice(0, 10);
    await dataService.createFollowup({ entityType: 'creator', entityId: creator.id, entityLabel: creator.fullName, title: newTitle.trim(), dueDate, priority: 'medium', ownerId: creator.ownerId });
    push(t('toast.followupScheduled'), 'success');
    setNewTitle('');
    setNewCreatorId('');
    setFormOpen(false);
    setSearchParams({});
    reload();
  }

  const visible = useMemo(() => followups.filter((f) => matchesView(f, view)).sort((a, b) => a.dueDate.localeCompare(b.dueDate)), [followups, view]);

  async function complete(f: Followup) {
    await dataService.completeFollowup(f.id);
    push(t('toast.followupCompleted'), 'success');
    reload();
  }
  async function reopen(f: Followup) {
    await dataService.reopenFollowup(f.id);
    reload();
  }
  async function confirmDelete() {
    if (!deleteTarget) return;
    await dataService.deleteFollowup(deleteTarget.id);
    push(t('toast.demoRecordDeleted'), 'error');
    setDeleteTarget(null);
    reload();
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>{t('nav.followups')}</h1>
        <button className="btn-primary" onClick={() => setFormOpen(true)}>+ {t('quickCreate.followup')}</button>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {VIEWS.map((v) => (
          <button key={v} onClick={() => setView(v)} className={view === v ? 'btn-primary' : ''}>{t(`followupViews.${v}`)}</button>
        ))}
      </div>

      <div className="card">
        {visible.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>{t('empty.noFollowups')}</div>
        ) : (
          visible.map((f) => (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
              <span className={`badge ${f.priority === 'high' ? 'crit' : f.priority === 'medium' ? 'warn' : 'info'}`}>{f.priority}</span>
              <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => navigate(f.entityType === 'creator' ? `/creators/${f.entityId}` : `/partnerships/${f.entityId}`)}>
                <div style={{ fontWeight: 600 }}>{f.title}</div>
                <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{f.entityLabel} · {t('followupForm.due')} {f.dueDate}</div>
              </div>
              {f.status === 'open' ? (
                <>
                  <button onClick={() => complete(f)}>{t('followupForm.markDone')}</button>
                  <button style={{ color: 'var(--crit)' }} onClick={() => setDeleteTarget(f)}>{t('rowMenu.deleteDemo')}</button>
                </>
              ) : (
                <button onClick={() => reopen(f)}>{t('followupForm.reopen')}</button>
              )}
            </div>
          ))
        )}
      </div>

      <ConfirmModal
        open={!!deleteTarget}
        title={t('confirm.deleteFollowupTitle')}
        recordName={deleteTarget?.title}
        description={t('confirm.deleteFollowupDesc')}
        reversible={false}
        tone="destructive"
        confirmLabel={t('rowMenu.deleteDemo')}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {formOpen && (
        <>
          <div className="drawer-backdrop" onClick={() => { setFormOpen(false); setSearchParams({}); }} />
          <div className="drawer-panel">
            <div style={{ fontWeight: 700, marginBottom: 12 }}>{t('quickCreate.followup')}</div>
            <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>{t('followupForm.creator')}</label>
            <select className="text-input" value={newCreatorId} onChange={(e) => setNewCreatorId(e.target.value)} style={{ marginBottom: 10 }}>
              <option value="">—</option>
              {creators.map((c) => <option key={c.id} value={c.id}>{c.id} — {c.fullName}</option>)}
            </select>
            <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>{t('followupForm.title')}</label>
            <input className="text-input" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} style={{ marginBottom: 14 }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-primary" onClick={submitNew}>{t('common.save')}</button>
              <button onClick={() => { setFormOpen(false); setSearchParams({}); }}>{t('common.cancel')}</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
